import * as React from 'react';
import type { IAuthMe } from 'src/models/interfaces/IAuthMe';
import { getAuthMe } from 'src/services/authMe.service';

interface AuthMeContextType {
  authMe: IAuthMe | null;
  loadAuthMe: () => Promise<void>;
  /** True if the user has a non-NoAccess role (hasAccess=true) or is admin. */
  canAccessModule: (moduleName: string) => boolean;
  /** True if the user has at least one feature assigned to the given subModuleCode (or isAdmin). */
  canAccessSubModule: (subModuleCode: string) => boolean;
  /** True if the user's role description is 'PycpCorporate' or 'Admin'. */
  canClosePeriod: () => boolean;
  /** True if the user has the given feature code in any submodule (or isAdmin). */
  hasFeature: (code: string) => boolean;
  /** True if the user has the given feature code specifically in the given subModuleCode (or isAdmin). */
  hasFeatureInSubModule: (code: string, subModuleCode: string) => boolean;
}

const AuthMeContext = React.createContext<AuthMeContextType>({
  authMe: null,
  loadAuthMe: async () => {},
  canAccessModule: () => true,
  canAccessSubModule: () => true,
  canClosePeriod: () => true,
  hasFeature: () => true,
  hasFeatureInSubModule: () => true,
});

export const AuthMeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authMe, setAuthMe] = React.useState<IAuthMe | null>(null);

  const loadAuthMe = React.useCallback(async () => {
    try {
      const data = await getAuthMe();
      setAuthMe(data);
    } catch {
      // Silently fail — real security is enforced by the backend.
    }
  }, []);

  // Auto-load on mount so any page refresh works, not just entry via HomePage.
  React.useEffect(() => { loadAuthMe(); }, [loadAuthMe]);

  const canAccessModule = React.useCallback((): boolean => {
    if (!authMe) return true;
    if (authMe.isAdmin) return true;
    // hasAccess = false → NoAccess role, no access to anything
    return authMe.hasAccess;
  }, [authMe]);

  const canAccessSubModule = React.useCallback((subModuleCode: string): boolean => {
    if (!authMe) return true; // not loaded yet — optimistic
    if (authMe.isAdmin) return true;
    if (!authMe.hasAccess) return false; // NoAccess role
    return authMe.features.some((f) => f.subModuleCode === subModuleCode);
  }, [authMe]);

  const canClosePeriod = React.useCallback((): boolean => {
    if (!authMe) return false;
    const desc = authMe.role.description;
    return desc === 'PycpCorporate' || desc === 'Admin';
  }, [authMe]);

  const hasFeature = React.useCallback((code: string): boolean => {
    if (!authMe) return true; // not loaded yet — optimistic
    if (authMe.isAdmin) return true;
    return authMe.features?.some((f) => f.code === code) ?? false;
  }, [authMe]);

  const hasFeatureInSubModule = React.useCallback((code: string, subModuleCode: string): boolean => {
    if (!authMe) return true; // not loaded yet — optimistic
    if (authMe.isAdmin) return true;
    return authMe.features?.some((f) => f.code === code && f.subModuleCode === subModuleCode) ?? false;
  }, [authMe]);

  return (
    <AuthMeContext.Provider value={{ authMe, loadAuthMe, canAccessModule, canAccessSubModule, canClosePeriod, hasFeature, hasFeatureInSubModule }}>
      {children}
    </AuthMeContext.Provider>
  );
};

export function useAuthMe(): AuthMeContextType {
  return React.useContext(AuthMeContext);
}
